import { timeToIndex, indexToTime } from "./timeIndex";
import { getDrawingTool } from "./drawingTools";
import { placeDrawingPoints } from "./drawingPlacement";

// A clone lies this far from its original, so it is seen as a separate drawing
const CLONE_SHIFT_PX = 20;

// Away from the right pane edge when the point is close to it: the clone stays in view
const shiftInsidePane = (position, size) => (position + CLONE_SHIFT_PX > size ? -CLONE_SHIFT_PX : CLONE_SHIFT_PX);

const shiftPoints = (points, { store, barDurationMs, timeScale, priceScale }) => {
  if (!store.length) return points.map((point) => ({ ...point }));

  const firstX = timeScale.indexToX(timeToIndex(points[0].time, store, barDurationMs));
  const barShift = shiftInsidePane(firstX, timeScale.width) / timeScale.barSpacing;
  const topY = Math.min(...points.map(({ price }) => priceScale.priceToY(price)));
  // Up (po-chart-visual-spec.md §7.5), down only when there is no room above
  const yShift = topY - CLONE_SHIFT_PX < 0 ? CLONE_SHIFT_PX : -CLONE_SHIFT_PX;

  return points.map(({ time, price }) => ({
    time: indexToTime(timeToIndex(time, store, barDurationMs) + barShift, store, barDurationMs),
    price: priceScale.yToPrice(priceScale.priceToY(price) + yShift),
  }));
};

/**
 * A new drawing from the menu and changes of one drawing from its toolbar (TV-19) and settings (TV-20).
 * A locked drawing only stops moving under the pointer (drawingPointer): these actions still work on it.
 *
 * @param {object} params
 * @param {object} params.model - createDrawingsModel
 * @param {() => object} params.getView - see createDrawings
 */
export const createDrawingEdits = ({ model, getView }) => {
  const findItem = (id) => model.items.find((item) => item.id === id);

  return {
    /**
     * Puts a drawing of the tool on the chart at its default places, not selected.
     *
     * @param {string} toolId - DRAWING_TOOL value
     * @returns {string|null} its id; null for an unknown tool or a chart without bars
     */
    create: (toolId) => {
      const tool = getDrawingTool(toolId);
      const points = tool && placeDrawingPoints(tool, getView());

      if (!points) return null;

      model.select(null);

      const { id } = model.add(tool.id, points, model.styleOfTool(tool));

      model.changed();

      return id;
    },

    /**
     * @param {string} id
     * @param {{ color?: string, width?: number }} change - CSS colour, CSS px
     * @returns {boolean} false for an unknown id
     */
    setStyle: (id, change) => {
      const item = findItem(id);

      if (!item) return false;

      item.style = { ...item.style, ...change };
      model.changed();

      return true;
    },

    /**
     * Save of the settings modal. A hidden drawing stays on the list but can no
     * longer be reached on the chart, so it loses the selection.
     *
     * @param {string} id
     * @param {{ style: object, isVisible: boolean }} settings - style: see setStyle, with the fields of drawingStyles.js
     * @returns {boolean} false for an unknown id
     */
    updateSettings: (id, { style, isVisible }) => {
      const item = findItem(id);

      if (!item) return false;

      item.style = { ...item.style, ...style };
      item.isVisible = isVisible;
      if (!isVisible && model.selectedId === id) model.select(null);
      model.changed();

      return true;
    },

    /**
     * @param {string} id
     * @param {boolean} isLocked
     * @returns {boolean} false for an unknown id
     */
    setLocked: (id, isLocked) => {
      const item = findItem(id);

      if (!item) return false;

      item.isLocked = isLocked;
      model.changed();

      return true;
    },

    /**
     * A copy next to the drawing, with its style, unlocked so it can be moved. The copy gets selected.
     *
     * @param {string} id
     * @returns {string|null} id of the copy; null for an unknown id
     */
    clone: (id) => {
      const item = findItem(id);

      if (!item) return null;

      const copy = model.add(item.type, shiftPoints(item.points, getView()), item.style);

      model.select(copy.id);
      model.invalidate();

      return copy.id;
    },
  };
};
